/** Lowercase, strip accents, drop everything that is not a letter or digit. */
export function normalizeSearch(input: string): string {
  return input
    .normalize('NFD')
    .replace(/\p{M}/gu, '')
    .toLowerCase()
    .replace(/[^\p{L}\p{N}]/gu, '');
}

export interface Searchable {
  readonly id: string;
  readonly name: string;
  readonly aliases?: readonly string[];
}

export interface SearchOptions {
  readonly excludeIds?: ReadonlySet<string>;
  readonly limit?: number;
}

/**
 * Type-ahead search. Ranks name prefix, then alias prefix, then name substring, then alias
 * substring; ties sort by name. An empty query lists everything alphabetically.
 */
export function searchChampions<T extends Searchable>(
  champions: readonly T[],
  query: string,
  options: SearchOptions = {},
): T[] {
  const q = normalizeSearch(query);
  const limit = options.limit ?? 8;
  const scored: { champion: T; rank: number; name: string }[] = [];
  for (const champion of champions) {
    if (options.excludeIds?.has(champion.id)) continue;
    const name = normalizeSearch(champion.name);
    let rank = q === '' ? 0 : -1;
    if (q !== '') {
      const aliases = (champion.aliases ?? []).map(normalizeSearch);
      if (name.startsWith(q)) rank = 0;
      else if (aliases.some((a) => a.startsWith(q))) rank = 1;
      else if (name.includes(q)) rank = 2;
      else if (aliases.some((a) => a.includes(q))) rank = 3;
    }
    if (rank >= 0) scored.push({ champion, rank, name });
  }
  scored.sort((a, b) => a.rank - b.rank || (a.name < b.name ? -1 : a.name > b.name ? 1 : 0));
  return scored.slice(0, limit).map((s) => s.champion);
}
